import { useCallback, useState } from "react";
import { ROUTES as API_ROUTES } from "../../constants/api";
import { GetApiData } from "../../api/general";
import useErrorModal from "../useErrorModal";

// types
import type { SendMoneyMobileWalletOperatorsResponse } from "../../types/api/sendMoney";
import type { MessageWithDirectives } from "../../types/api/general";
import type { MobileWalletOperator } from "../../types/sendMoney";
import type { DisclaimerType } from "../../types/api/moneyTransfer";

const useGetMobileWalletOperators = () => {
  const openErrorModal = useErrorModal();
  const [loading, setLoading] = useState(false);
  const [operators, setOperators] = useState<MobileWalletOperator[]>([]);
  const [disclaimers, setDisclaimers] = useState<DisclaimerType[]>([]);
  const [primaryDisclaimers, setPrimaryDisclaimers] = useState<MessageWithDirectives[]>([]);

  // Stable identity — useErrorModal returns a fresh function each render, so
  // memoizing on it would re-fire the modal effect every render (infinite
  // loop). URL is constant, nothing closed over changes: empty deps is correct.
  const fetchOperators = useCallback(async (quoteId: number) => {
    try {
      setLoading(true);

      const url = API_ROUTES.SEND_MONEY.GET_MOBILE_WALLET_OPERATORS(quoteId);
      const response =
        await GetApiData<SendMoneyMobileWalletOperatorsResponse>(url);

      // the server sends icon paths relative to the API; resolve once here so the
      // modal and the stored selectedOperator both carry a usable URL
      const resolvedOperators = (response.result.operators ?? []).map((operator) => ({
        ...operator,
        iconUrl: API_ROUTES.SEND_MONEY.ICON(operator.iconUrl),
      }));

      setOperators(resolvedOperators);
      setDisclaimers(response.result.disclaimers ?? []);
      setPrimaryDisclaimers(response.result.primaryDisclaimers ?? []);
    } catch (err) {
      openErrorModal(err);
    } finally {
      setLoading(false);
    }
  }, []);

  return { loading, operators, disclaimers, primaryDisclaimers, fetchOperators };
};

export default useGetMobileWalletOperators;